import {FontWeightProperty} from "csstype";
import {CSSProperties} from "react";
import {IStateUI} from "./store/StoreInterface";

export interface IData {
	links: ILinks;
	content: IContent[];
}

export interface IScrollyContainer {
	bounce: boolean;
	content: IContent;
	data: IData;
	error: boolean;
	id: number;
	isFetching: boolean;
	mediaLoaded: boolean;
	muted: boolean;
	navigationClicked: boolean;
	onDisablePullToRefresh: (val: boolean) => void;
	onGetBounce: (val: boolean) => void;
	onGetContent: (val: IContent) => void;
	onMediaLoaded: (val: boolean) => void;
	onNavigationFinished: () => void;
	onScrollFinished: () => void;
	onSetScrollDirection: (val: "up" | "down") => void;
	onToggleSideDrawer: (val: boolean) => void;
	onUnmuted: (val: boolean) => void;
	onVideoLoaded: (val: boolean) => void;
	pullToRefreshDisabled: boolean;
	scrollClicked: boolean;
	scrollDirection: "up" | "down";
	showSideDrawer: boolean;
	videoLoaded: boolean;
}

export interface IContent {
	background_position?: "left" | "center" | "right";
	header: IText;
	footer: IText;
	id: number;
	type: "picture" | "video";
	link?: IMediaLink;
	linkMobile?: IMediaLink;
	pureGreenButton?: IPureGreenButton;
	extraInformationIcons?: [];
	paragraphs?: IText[];
	poster: string;
	thumbnail: string;
	color?: string;
}

export interface IMediaLink {
	mp4: string;
	ogv: string;
	webm: string;
	src: string;
}

export interface IPureGreenButton {
	breakpoint: IBreakpoint;
	link: string;
	positions: IStyle;
}

export interface IText {
	background_desktop?: string;
	background_mobile?: string;
	color?: string;
	font?: string;
	font_size?: string;
	font_weight?: FontWeightProperty;
	font_style?: "normal" | "italic" | "oblique";
	align?: "left" | "center" | "right";
	letter_spacing?: string;
	line_height?: string;
	margin?: string;
	margin_bottom?: string;
	text: string;
	link?: string;
	type?: "title" | "paragraph";
	width?: string;
}

export interface IScrollyComponent {
	bounce: boolean;
	content: IContent;
	elemNumber: number;
	footer: IText;
	header: IText;
	id: number;
	links: ILinks;
	mediaLoaded: boolean;
	muted: boolean;
	navImages: string[];
	onMediaLoaded: (val: boolean) => void;
	onVideoLoaded: (val: boolean) => void;
	scrollDirection: "up" | "down";
	showSideDrawer: boolean;
	videoLoaded: boolean;
}

export interface ILinks {
	home: string;
	facebook: string;
	portal: string;
	twitter: string;
}

export interface INavigationContainer {
	content: IContent;
	navImages: string[];
	navType: string;
	onNavigationClicked: (id: number) => void;
}

export interface INavigationComponent {
	contentId: number;
	navImages: string[];
	navType: string;
	onNavigationClicked: (id: number) => void;
	color: string;
}

export interface INavTypeObj {
	desktop: string;
	mobile: string;
}

export interface ISideDrawerComponent {
	contentId: number;
	closed: () => void;
	links: ILinks;
	open: boolean;
	navImages: string[];
	navType: string;
	color: string;
}

export interface IBackdrop {
	clicked: () => void;
	children?: JSX.Element;
	show: boolean;
}

export interface ISocialComponent {
	links: ILinks;
	navType: string;
	color: string;
}

export interface ISpinner {
	hide: boolean;
}

export interface IFooterContainer {
	bounce: boolean;
	breakpoint: IBreakpoint;
	content: IContent;
	elemNumber: number;
	muted: boolean;
	onSoundClicked: (val: boolean) => void;
	onScrollClicked: () => void;
	showSideDrawer: boolean;
	videoLoaded: boolean;
}

export interface IBreakpoint {
	breakpointName: string;
}

export interface IMediaQuery {
	name: string;
	query: MediaQueryList;
}

export interface IFooterComponent {
	bounce: boolean;
	breakpoint: IBreakpoint;
	contentId: number;
	elemNumber: number;
	footer: IText;
	muted: boolean;
	onSoundClicked: (val: boolean) => void;
	onScrollClicked: () => void;
	showSideDrawer: boolean;
	videoLoaded: boolean;
}

export interface IFooterButton {
	bounce: boolean;
	styles: CSSProperties;
	contentId: number;
	elemNumber: number;
	footer: IText;
	onScrollClicked: () => void;
	videoLoaded: boolean;
}

export interface IHeaderContainer {
	breakpoint: IBreakpoint;
	content: IContent;
	links: ILinks;
	navImages: string[];
	navType: INavTypeObj;
	onToggleSideDrawer: (val: boolean) => void;
	showSideDrawer: boolean;
}

export interface IHeaderComponent {
	breakpoint: IBreakpoint;
	contentId: number;
	header: IText;
	links: ILinks;
	navImages: string[];
	navType: INavTypeObj;
	onToggleSideDrawer: (val: boolean) => void;
	showSideDrawer: boolean;
}

export interface IHeaderButton {
	header: IText;
	links: ILinks;
	styles: CSSProperties;
	showSideDrawer: boolean;
}

export interface ITextContainer {
	breakpoint: IBreakpoint;
	content: IContent;
	mediaLoaded: boolean;
	scrollDirection: "up" | "down";
	showSideDrawer: boolean;
}

export interface IBackgroundContainer {
	breakpoint: IBreakpoint;
	content: IContent;
	mediaLoaded: boolean,
	muted: boolean,
	onMediaLoaded: (val: boolean) => void,
	onVideoLoaded: (val: boolean) => void
	scrollDirection: "up" | "down",
	videoLoaded: boolean
}

export interface ITextComponent {
	breakpoint: IBreakpoint;
	content: IContent;
	mediaLoaded: boolean;
	scrollDirection: "up" | "down";
	showSideDrawer: boolean;
}

export interface IInformationComponent {
	content: IContent;
	readonly ui: IStateUI;
}

export interface IPosition {
	top?: string;
	bottom?: string;
	left?: string;
	right?: string;
}

export interface IExtraInformationComponent {
	bounce: boolean;
	breakpoint: IBreakpoint;
	muted: boolean;
	color: string;
	position: IStyle;
	pullToRefreshDisabled: boolean;
	showSideDrawer: boolean;
	text: string;
	videoLoaded: boolean;
}

export interface IBackgroundComponent {
	content: IContent;
	breakpoint: IBreakpoint;
	mediaLoaded: boolean;
	muted: boolean;
	onMediaLoaded: (val: boolean) => void;
	onVideoLoaded: (val: boolean) => void;
	scrollDirection: "up" | "down";
	videoLoaded: boolean;
}

export interface IAppWrapper {
	onSetActiveBreakpoint: (str: string) => void;
}

export interface IStyle {
	default?: string | IPosition;
	desktopLg?: string | number | IPosition;
	desktopMd?: string | number | IPosition;
	desktopSm?: string | number | IPosition;
	tabletLg?: string | number | IPosition;
	tabletMd?: string | number | IPosition;
	tabletSm?: string | number | IPosition;
	mobileLg?: string | number | IPosition;
	mobileMd?: string | number | IPosition;
	mobileSm?: string | number | IPosition;
}

export enum Colors {
	white = "#fff",
	black = "#000"
}



// WEBPACK FOOTER //
// ./src/ScrollyInterface.tsx